import compact from 'lodash/compact';
import { useMemo } from 'react';

import { MedicalInformationType } from '@dreryk/egabinet-patient-medical-information-frontend-api';

import { BindingCreator, useTranslate } from '@dreryk/react-common';
import { Select } from '@dreryk/react-components';

import { protectFeature } from '~libs.shared.system';

import {
   medicalDocumentationCreateTranslationKeys,
   patientMedicalInformationTranslationKeys,
} from '../../../translations';

const MEDICAL_INFORMATION_TYPE_FIELD_NAME = 'medicalInformationType';

type MedicalInformationTypeFormBeanPart = {
   [MEDICAL_INFORMATION_TYPE_FIELD_NAME]: MedicalInformationType | null;
};

type MedicalInformationTypeFieldProps = {
   readonly register: BindingCreator<MedicalInformationTypeFormBeanPart>;
   readonly availableMedicalInformationTypes: MedicalInformationType[] | undefined;
};

const useTriggerLabelExtractor = (): ((item?: MedicalInformationType) => string) => {
   const translate = useTranslate();
   return (item): string => (item ? translate(medicalDocumentationCreateTranslationKeys.categories[item]) : '');
};

export const MedicalInformationTypeField = (props: MedicalInformationTypeFieldProps): JSX.Element | null => {
   const triggerLabelExtractor = useTriggerLabelExtractor();
   const creatingGynecologicalInterviewAvailable = protectFeature('creatingGynecologicalInterview');

   const medicalInformationTypes: MedicalInformationType[] = useMemo(
      () =>
         compact([
            'ANTHROPOMETRY',
            'CHRONIC_DISEASE',
            'RISK_FACTOR',
            'HOSPITALIZATION',
            'MEDICAL_CONSULTATION',
            'REHABILITATION',
            'REFERRAL',
            'ALLERGY',
            'ANALYTICAL_TEST_RESULT',
            'MEDICAL_IMAGING',
            creatingGynecologicalInterviewAvailable ? 'GYNECOLOGICAL_INTERVIEW' : undefined,
            'TREATMENT',
            'OTHER',
         ]),
      [creatingGynecologicalInterviewAvailable]
   );

   const availableMedicalInformationTypes = useMemo(
      () => props.availableMedicalInformationTypes?.filter((item) => medicalInformationTypes.includes(item)),
      [medicalInformationTypes, props.availableMedicalInformationTypes]
   );

   const selectionDisabled = Boolean(props.availableMedicalInformationTypes?.length === 1);

   const selectionItems = useMemo(
      () =>
         availableMedicalInformationTypes && availableMedicalInformationTypes.length > 0
            ? availableMedicalInformationTypes
            : medicalInformationTypes,
      [medicalInformationTypes, availableMedicalInformationTypes]
   );

   return (
      <Select<MedicalInformationType>
         testId='medicalInformationType'
         label={patientMedicalInformationTranslationKeys.addEntryModal.entryCategory}
         placeholder={patientMedicalInformationTranslationKeys.addEntryModal.chooseMedicalInformationType}
         triggerLabelExtractor={triggerLabelExtractor}
         disabled={selectionDisabled}
         controller={props.register(MEDICAL_INFORMATION_TYPE_FIELD_NAME, { required: true })}
      >
         <Select.List<MedicalInformationType> items={selectionItems}>
            {(item: MedicalInformationType) => (
               <Select.Item
                  item={item}
                  testId={item}
                  key={item}
                  label={medicalDocumentationCreateTranslationKeys.categories[item]}
               />
            )}
         </Select.List>
      </Select>
   );
};
